import React, { useCallback, useState, useEffect, useMemo } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tabs, Tab,
  Checkbox, IconButton, Tooltip, Chip, Typography, CircularProgress,
} from "@mui/material";
import supabase from "../../../../../utils/supabase";
import { useStatusDialog } from "../../../../common/StatusDialogProvider";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LinkIcon from '@mui/icons-material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

import { GBIF_BACKBONE_DATASET_KEY, CHECKLIST_BANK_DATASET_KEY, dataGridStyles } from "./constants";
import { createSelectionModel, getGbifAuthorship, getGbifDisplayName, getGbifYear, lineBg, normalizeNameForComparison, normalizeWhitespace, wait } from "./utils/gbifUtils";
import Step1SelectDbParent from "./components/Step1SelectDbParent";
import Step2SelectGbif from "./components/Step2SelectGbif";
import Step3FetchConfig from "./components/Step3FetchConfig";
import Step4Review from "./components/Step4Review";
import Step5Result from "./components/Step5Result";
import LinkDialog from "./components/LinkDialog";
import ViewDialog from "./components/ViewDialog";
import ConfirmDialog from "./components/ConfirmDialog";

export default function DialogGbifHierarchicalImport({ open, onClose }) {
  const { showStatus } = useStatusDialog();

  // Step-based state
  const [activeStep, setActiveStep] = useState(0);
  const [dbParent, setDbParent] = useState(null); // Step 1 result
  const [gbifParentSelection, setGbifParentSelection] = useState(() => createSelectionModel()); // Step 2 result
  const [registrationResult, setRegistrationResult] = useState(null); // Step 4 result for Step 5
  const [fetchRequested, setFetchRequested] = useState(false);

  // Existing states
  const [history, setHistory] = useState([]);
  const [currentParent, setCurrentParent] = useState(null); // Constructed in Step 3, used in Step 4
  const [reviewCandidates, setReviewCandidates] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // GBIF search states (for Step 2)
  const [gbifSearchLoading, setGbifSearchLoading] = useState(false);
  const [gbifSearchResults, setGbifSearchResults] = useState([]);
  const [gbifSearchError, setGbifSearchError] = useState("");
  const [gbifSortOrder, setGbifSortOrder] = useState('recommended'); // 'recommended', 'freshness'
  const [gbifFreshnessFilter, setGbifFreshnessFilter] = useState('all'); // 'all', 'fresh', 'stale', 'archived'

  // Fetch settings (for Step 3)
  const [fetchMode, setFetchMode] = useState('self_only');
  const [fetchDepth, setFetchDepth] = useState(1); // 1, 2, ..., 10 (for 'all')

  // DB parent search states (for Step 1)
  const [initialParentSearchQuery, setInitialParentSearchQuery] = useState("");
  const [initialParentSearchResults, setInitialParentSearchResults] = useState([]);
  const [initialParentSearchLoading, setInitialParentSearchLoading] = useState(false);

  // State for manual linking
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [candidateToLink, setCandidateToLink] = useState(null);

  // View / edit dialog for merged entries
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTargetRow, setViewTargetRow] = useState(null);
  const [viewSelection, setViewSelection] = useState([]);
  
  // Confirmation Dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogContent, setConfirmDialogContent] = useState({ title: '', description: '', onConfirm: null });

  // State for search in Manual Link Dialog
  const [linkDialogSearchQuery, setLinkDialogSearchQuery] = useState("");
  const [linkDialogSearchResults, setLinkDialogSearchResults] = useState([]);
  const [linkDialogSearchLoading, setLinkDialogSearchLoading] = useState(false);

  const [datasetMetaCache, setDatasetMetaCache] = useState(new Map());
  const [datasetMetaPending, setDatasetMetaPending] = useState(0);

  const resetState = useCallback(() => {
    setActiveStep(0);
    setDbParent(null);
    setGbifParentSelection(createSelectionModel());
    setRegistrationResult(null);
    setFetchRequested(false);

    setHistory([]);
    setCurrentParent(null);
    setReviewCandidates([]);
    setSelectionModel([]);
    setLoading(false);
    setError("");

    setGbifSearchLoading(false);
    setGbifSearchResults([]);
    setGbifSearchError("");

    setInitialParentSearchQuery("");
    setInitialParentSearchResults([]);
    setInitialParentSearchLoading(false);

    setLinkDialogOpen(false);
    setCandidateToLink(null);
    setLinkDialogSearchQuery("");
    setLinkDialogSearchResults([]);
    setLinkDialogSearchLoading(false);

    setViewDialogOpen(false);
    setViewTargetRow(null);
    setViewSelection([]);
    setConfirmDialogOpen(false);
    setDatasetMetaCache(new Map());
  }, []);

  const searchInitialParent = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setInitialParentSearchResults([]);
      return;
    }
    setInitialParentSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from('scientific_names')
        .select('id, name_spell_valid, current_rank, parent:current_parent(name_spell_valid)')
        .ilike('name_spell_valid', `%${query}%`)
        .limit(20);
      if (error) throw error;
      setInitialParentSearchResults(data || []);
    } catch (err) {
      console.error("Failed to search for initial parent:", err);
    } finally {
      setInitialParentSearchLoading(false);
    }
  }, []);

  const searchDbNames = useCallback(async (query, setter, loadingSetter) => {
    if (!query || query.length < 2) {
      setter([]);
      return;
    }
    loadingSetter(true);
    try {
      const { data, error } = await supabase
        .from('scientific_names')
        .select('id, name_spell_valid, current_rank, parent:current_parent(name_spell_valid)')
        .ilike('name_spell_valid', `%${query}%`)
        .limit(20);
      if (error) throw error;
      setter(data || []);
    } catch (err) {
      console.error("Failed to search for DB names:", err);
      setError("データベースの学名検索に失敗しました: " + err.message);
    } finally {
      loadingSetter(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if(initialParentSearchQuery) searchInitialParent(initialParentSearchQuery);
      else setInitialParentSearchResults([]);
    }, 300); // 300ms debounce

    return () => clearTimeout(handler);
  }, [initialParentSearchQuery, searchInitialParent]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (linkDialogOpen && linkDialogSearchQuery) {
        searchDbNames(linkDialogSearchQuery, setLinkDialogSearchResults, setLinkDialogSearchLoading);
      } else setLinkDialogSearchResults([]);
    }, 300);
    return () => clearTimeout(handler);
  }, [linkDialogSearchQuery, linkDialogOpen, searchDbNames]);

  const fetchDatasetMetadata = useCallback(async (keys) => {
    const newKeys = [...new Set(keys.filter(key => key && !datasetMetaCache.has(key)))];
    if (newKeys.length === 0) return;

    const tempCache = new Map(datasetMetaCache);
    newKeys.forEach(key => tempCache.set(key, { loading: true, title: key }));
    setDatasetMetaCache(tempCache);
    setDatasetMetaPending((p) => p + 1);

    const results = await Promise.all(newKeys.map(async (key) => {
        try {
            const response = await fetch(`https://api.gbif.org/v1/dataset/${key}`);
            if (!response.ok) {
                console.warn(`[GBIF Dataset Meta] Failed to fetch meta for key ${key}. Status: ${response.status}`);
                return { key, meta: { title: key, error: true, errorMessage: `Status ${response.status}` } };
            }
            const meta = await response.json();
            console.log(`[GBIF Dataset Meta] Key: ${key}`, meta);
            return { key, meta };
        } catch (error) {
            console.error(`[GBIF Dataset Meta] Fetch error for key ${key}:`, error);
            return { key, meta: { title: key, error: true, errorMessage: error?.message || "Failed to fetch" } };
        }
    }));
    setDatasetMetaCache(prev => new Map([...prev, ...results.map(r => [r.key, r.meta])]));
    setDatasetMetaPending((p) => Math.max(0, p - 1));
  }, [datasetMetaCache]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const searchGbif = useCallback(async (query) => {
    setGbifSearchLoading(true);
    setGbifSearchError("");
    setGbifSearchResults([]);
    setGbifParentSelection(createSelectionModel());
    try {
      // Promise.allで並行して取得
      const [searchResponse, matchResult] = await Promise.all([
        // 1. Direct API call to species endpoint, which is confirmed to work
        fetch(`https://api.gbif.org/v1/species?name=${encodeURIComponent(query)}&limit=20`).then(res => res.ok ? res.json() : null),
        // 2. バックボーンを確実に取得するための直接一致検索 (species/match)
        fetch(`https://api.gbif.org/v1/species/match?name=${encodeURIComponent(query)}&verbose=true`).then(res => res.ok ? res.json() : null)
      ]);

      // 1. searchの結果を処理
      let searchResolved = [];
      if (searchResponse && Array.isArray(searchResponse.results)) {
        searchResolved = searchResponse.results;
      } else if (searchResponse) {
        console.warn("GBIF search API call returned unexpected format.", searchResponse);
      } else {
        console.warn("GBIF search API call failed.");
      }

      // 2. matchの結果を処理
      let matchResolved = [];
      if (matchResult && matchResult.matchType !== 'NONE') {
        // `usage` が最も一致度が高い候補
        if (matchResult.usage) {
          matchResolved.push(matchResult.usage);
        }
        // `alternatives` に他の候補が含まれる
        if (Array.isArray(matchResult.alternatives)) {
          matchResolved.push(...matchResult.alternatives);
        }
      }

      // 3. 両方の結果をマージして重複を排除 (matchResultを優先)
      const allResults = [...searchResolved, ...matchResolved];
      const uniqueResults = Array.from(new Map(allResults.map(item => [item.key, item])).values());

      if (uniqueResults.length === 0) {
        setGbifSearchError("GBIF検索で候補が見つかりませんでした。");
        setGbifSearchResults([]);
      } else {
        setGbifSearchResults(uniqueResults);
      }
    } catch (err) {
      setGbifSearchError(err.message || "GBIF検索に失敗しました。");
    } finally {
      setGbifSearchLoading(false);
    }
  }, []);

  const searchGbifByName = useCallback(async (query) => {
    try {
      // Promise.allで並行して取得
      const [searchResponse, matchResult] = await Promise.all([
        // 1. Direct API call to species endpoint
        fetch(`https://api.gbif.org/v1/species?name=${encodeURIComponent(query)}&limit=100`).then(res => res.ok ? res.json() : null),
        // 2. バックボーンを確実に取得するための直接一致検索 (species/match)
        fetch(`https://api.gbif.org/v1/species/match?name=${encodeURIComponent(query)}&verbose=true`).then(res => res.ok ? res.json() : null)
      ]);

      let searchResolved = [];
      if (searchResponse && Array.isArray(searchResponse.results)) {
        searchResolved = searchResponse.results;
      } else if (searchResponse) {
        console.warn("GBIF search API call returned unexpected format.", searchResponse);
      }

      let matchResolved = [];
      if (matchResult && matchResult.matchType !== 'NONE') {
        if (matchResult.usage) matchResolved.push(matchResult.usage);
        if (Array.isArray(matchResult.alternatives)) matchResolved.push(...matchResult.alternatives);
      }

      // 3. 両方の結果をマージして重複を排除 (matchResultを優先)
      const allResults = [...matchResolved, ...searchResolved];
      const uniqueResults = Array.from(new Map(allResults.map(item => [item.key, item])).values());
      return uniqueResults;

    } catch (err) {
      console.error(`GBIF name search failed for "${query}":`, err);
      return [];
    }
  }, []);

  const getDatasetName = useCallback((record) => {
    const key = record?.datasetKey;
    if (!key) return 'N/A';
    const meta = datasetMetaCache.get(key) || {};
    if (meta?.error) return `${meta.title || key} (meta fetch failed)`;
    if (meta?.title && !meta.loading) return meta.title;
    if (key === GBIF_BACKBONE_DATASET_KEY) return 'GBIF Backbone Taxonomy';
    if (key === CHECKLIST_BANK_DATASET_KEY) return 'Checklist Bank';
    return key;
  }, [datasetMetaCache]);

  const getFreshnessCategory = useCallback((record) => {
    const meta = datasetMetaCache.get(record.datasetKey);
    if (!meta?.modified) return 'unknown';
    const modifiedDate = new Date(meta.modified);
    const daysSinceModified = (new Date() - modifiedDate) / (1000 * 60 * 60 * 24);
    if (daysSinceModified <= 30) return 'fresh';
    if (daysSinceModified <= 365) return 'stale';
    return 'archived';
  }, [datasetMetaCache]);

  const displayedGbifResults = useMemo(() => {
    let results = [...gbifSearchResults];

    // Filter
    if (gbifFreshnessFilter !== 'all') {
      results = results.filter(r => getFreshnessCategory(r) === gbifFreshnessFilter);
    }

    // Sort
    results.sort((a, b) => {
      const aMeta = datasetMetaCache.get(a.datasetKey) || {};
      const bMeta = datasetMetaCache.get(b.datasetKey) || {};

      if (gbifSortOrder === 'recommended') {
        const aTitle = (aMeta.title || "").toLowerCase();
        const bTitle = (bMeta.title || "").toLowerCase();
        const aIsBackbone = a.datasetKey === GBIF_BACKBONE_DATASET_KEY || aTitle.includes("gbif backbone taxonomy");
        const bIsBackbone = b.datasetKey === GBIF_BACKBONE_DATASET_KEY || bTitle.includes("gbif backbone taxonomy");
        if (aIsBackbone && !bIsBackbone) return -1;
        if (!aIsBackbone && bIsBackbone) return 1;

        const aIsChecklist = a.datasetKey === CHECKLIST_BANK_DATASET_KEY || aTitle.includes("checklist bank");
        const bIsChecklist = b.datasetKey === CHECKLIST_BANK_DATASET_KEY || bTitle.includes("checklist bank");
        if (aIsChecklist && !bIsChecklist) return -1;
        if (!aIsChecklist && bIsChecklist) return 1;
      }
      const dateA = aMeta.modified ? new Date(aMeta.modified) : new Date(0);
      const dateB = bMeta.modified ? new Date(bMeta.modified) : new Date(0);
      return dateB - dateA; // Newest first
    });
    return results;
  }, [gbifSearchResults, gbifSortOrder, gbifFreshnessFilter, datasetMetaCache, getFreshnessCategory]);

  useEffect(() => {
    if (gbifSearchResults.length > 0) {
        const keys = [...new Set(gbifSearchResults.map(r => r.datasetKey).filter(Boolean))];
        fetchDatasetMetadata(keys);
    }
  }, [gbifSearchResults, fetchDatasetMetadata]);

  useEffect(() => {
    const keys = [...new Set(reviewCandidates.flatMap(row => (row.gbif_records || []).map(g => g.datasetKey).filter(Boolean)))];
    if (keys.length > 0) fetchDatasetMetadata(keys);
  }, [reviewCandidates, fetchDatasetMetadata]);

  const getDescendantCandidates = useCallback(async (parent, depth) => {
    // 渡されたparentオブジェクトのキーを直接使用して、DBへの不要な再問い合わせを避ける
    // これにより、UIで選択されたばかりでまだDBに保存されていないGBIFキーも正しく扱える
    const parentKeys = [];
    const appendParentKey = (k) => {
      if (!k) return;
      if (Array.isArray(k)) {
        k.forEach(item => {
          const strItem = String(item);
          if (!parentKeys.includes(strItem)) parentKeys.push(strItem);
        });
      } else {
        const str = String(k);
        if (!parentKeys.includes(str)) parentKeys.push(str);
      }
    };
    appendParentKey(parent.gbif_usage_key);
    if (Array.isArray(parent.gbif_key)) parent.gbif_key.forEach(appendParentKey);
    (parent.merged_gbif_ids || []).forEach(appendParentKey);

    if (parentKeys.length === 0) {
      // In 'descendants_only' mode, this is an error. In 'self_and_descendants', it's a non-fatal warning.
      if (fetchMode === 'descendants_only') {
        setError("親分類群に紐づくGBIFキーが見つかりません。");
      } else {
        console.warn("親分類群に紐づくGBIFキーが見つかりません。配下分類群は取得できません。");
      }
      return [];
    }

    try {
      // Step 1: Recursively fetch all descendants from GBIF
      const allDescendants = [];
      const seenGbifKeys = new Set();

      for (const parentKey of parentKeys) {
        let cursor = null;
        let safety = 0;

        while (safety < 400) { // Safety break to prevent infinite loops
          safety++;
          const { data, error: err } = await supabase.functions.invoke("gbif-tree-fetch", {
            body: {
              key: parentKey,
              depth: depth,
              limitPerLevel: 500,
              cursor: cursor,
              includeSynonyms: true,
            },
          });
          if (err) throw err;
          
          const resolved = (typeof data === 'string' ? JSON.parse(data) : data);
          if (!resolved) break;

          const descendants = resolved.descendants || resolved.items || [];
          if (descendants.length > 0) {
            descendants.forEach(d => {
              const gbifKey = d?.key != null ? String(d.key) : null;
              if (gbifKey && seenGbifKeys.has(gbifKey)) return;
              if (gbifKey) seenGbifKeys.add(gbifKey);
              allDescendants.push(d);
            });
          }
          
          cursor = resolved.nextCursor || resolved.cursor;
          if (!cursor) break;
          await wait(150); // Be nice to the API
        }
      }

      const normalizeParentKey = (name, { fallbackToRoot = false } = {}) => {
        const normalized = normalizeNameForComparison(name);
        if (normalized) return normalized;
        const first = normalizeWhitespace(String(name || '')).toLowerCase().split(/\s+/)[0] || null;
        if (fallbackToRoot) return first || 'root';
        return first || null;
      };

      const gbifChildrenRaw = allDescendants
        .filter(g => (g.rank || '').toUpperCase() !== 'UNRANKED')
        .map(g => {
          // GBIFレコード自身の親情報(g.parent)を優先し、なければ現在の検索コンテキストの親(parent.name)をフォールバックとして使用する
          const parentNameForNormalization = g.genus || g.parent || parent.name;
          const childName = g.canonicalName || g.scientificName;
          const normalizedChild = normalizeNameForComparison(childName, parentNameForNormalization);
          const normalizedParent = normalizeParentKey(parentNameForNormalization, { fallbackToRoot: false });
          return {
            ...g,
            _normalized_child: normalizedChild,
            _normalized_parent: normalizedParent,
          };
        })
        .filter(g => g._normalized_child); // 除外条件に引っかかったものは捨てる

      // 子供側の parentKey に対応する正規化名を引けるようにマップを作成（属など中間階級を正しく親にできるように）
      const gbifKeyToNormalized = new Map();
      gbifChildrenRaw.forEach(g => {
        const key = String(g.key);
        if (!gbifKeyToNormalized.has(key) && g._normalized_child) {
          gbifKeyToNormalized.set(key, {
            child: g._normalized_child,
            parent: g._normalized_parent,
          });
        }
      });

      const gbifChildren = gbifChildrenRaw.map(g => {
        const normalizedParentFromGbif = g.parentKey ? gbifKeyToNormalized.get(String(g.parentKey)) : null;
        const normalizedParent = normalizedParentFromGbif?.child || g._normalized_parent || null;
        return { ...g, _normalized_parent: normalizedParent };
      });

      // Step 2: 名寄せプール（親名×子名のキー）
      const gbifPool = new Map();
      gbifChildren.forEach(g => {
        // シノニムは acceptedKey でマージせず、自身のキーをグループ化キーにする
        const isSynonym = g.taxonomicStatus?.toLowerCase().includes('synonym');
        const parentKeyPart = g._normalized_parent ?? '';
        const key = isSynonym
          ? `syn-${parentKeyPart}||${g._normalized_child}||${g.key}`
          : `${parentKeyPart}||${g._normalized_child}`;
        if (!gbifPool.has(key)) gbifPool.set(key, []);
        gbifPool.get(key).push(g);
      });

      // Step 3: Recursively fetch all DB descendants
      let dbRecords = [];
      let parentIdsToFetch = [parent.db_id];
      let fetchedDbIds = new Set();
      let dbSafety = 0;
      while (parentIdsToFetch.length > 0 && dbSafety < 20) { // safety break for depth
          dbSafety++;
          const { data: children, error: dbErr } = await supabase
              .from('scientific_names')
              .select('id, name_spell_valid, gbif_id, gbif_usage_key, merged_gbif_ids, authors_for_search, authority_year, current_rank, current_parent')
              .in('current_parent', parentIdsToFetch);

          if (dbErr) throw dbErr;

          parentIdsToFetch = [];
          if (children && children.length > 0) {
              const newChildren = children.filter(c => !fetchedDbIds.has(c.id));
              newChildren.forEach(c => fetchedDbIds.add(c.id));
              dbRecords.push(...newChildren);
              parentIdsToFetch.push(...newChildren.map(c => c.id));
          }
      }

      const dbParentIds = [...new Set(dbRecords.map(r => r.current_parent).filter(Boolean))];
      let parentNamesMap = new Map();
      if (dbParentIds.length > 0) {
          const { data: parentNames, error: parentsError } = await supabase
              .from('scientific_names')
              .select('id, name_spell_valid')
              .in('id', dbParentIds);
          if (parentsError) throw parentsError;
          parentNamesMap = new Map(parentNames.map(p => [p.id, p.name_spell_valid]));
      }
      parentNamesMap.set(parent.db_id, parent.name);
 
      // Step 4: レビュー候補を生成
      // DB側の正規化名を付与
      const dbRecordsWithNormalized = dbRecords.map(db => {
        const parentName = parentNamesMap.get(db.current_parent) || '';
        const normalizedChild = normalizeNameForComparison(db.name_spell_valid || '', parentName);
        const normalizedParent = normalizeParentKey(parentName);
        return {
          ...db,
          parent_name: parentName,
          _normalized_child: normalizedChild,
          _normalized_parent: normalizedParent,
        };
      }).filter(db => db._normalized_child);

      // 名寄せプール: 親×子キーで GBIF/DB を突き合わせる
      const pool = new Map();

      // 1) DB側をプールに入れる
      dbRecordsWithNormalized.forEach(db => {
        const key = `${db._normalized_parent}||${db._normalized_child}`;
        if (!pool.has(key)) {
          pool.set(key, { gbif: [], db: [] });
        }
        pool.get(key).db.push(db);
      });

      // 2) GBIF側をプールに入れる
      gbifChildren.forEach(g => {
        // シノニムは別キーで扱う（同名シノニムはまとめるが accepted とマージしない）
        const isSynonym = g.taxonomicStatus?.toLowerCase().includes('synonym');
        const key = isSynonym
          ? `syn-${g._normalized_parent}||${g._normalized_child}`
          : `${g._normalized_parent}||${g._normalized_child}`;
        if (!pool.has(key)) {
          pool.set(key, { gbif: [], db: [] });
        }
        pool.get(key).gbif.push(g);
      });

      // プール内で DB が存在する行から「正規化された自名(=キーの子) -> DB ID」の逆引きマップを作成
      const resolvedParentIdMap = new Map();
      pool.forEach((entry, key) => {
        const [normalizedParent, normalizedChild] = key.split('||');
        const dbRow = entry.db?.[0];
        if (dbRow?.id) {
          // 子側で自分自身を引く用（正規化名→DB ID）
          if (normalizedChild) {
            resolvedParentIdMap.set(normalizedChild, dbRow.id);
          }
        }
      });

      // 3) プールから候補生成（ランク順に処理しつつ親IDを逐次解決）
      const candidates = [];
      const rankOrder = [
        'subfamily', 'supertribe', 'tribe', 'subtribe',
        'genus', 'subgenus', 'species', 'subspecies', 'variety', 'form', 'forma'
      ];
      const validRankSet = new Set(rankOrder);

      const entries = Array.from(pool.entries()).map(([key, entry]) => {
        const [normalizedParent, normalizedChild] = key.split('||');
        const dbEntry = entry.db[0] || null;
        const gbifGroup = entry.gbif || [];
        const primaryGbif = gbifGroup[0] || null;
        const rank = (dbEntry?.current_rank || primaryGbif?.rank || '').toLowerCase();
        return { key, entry, normalizedParent, normalizedChild, dbEntry, gbifGroup, primaryGbif, rank };
      });

      // 子のスペルごとに親候補を集計し、acceptedKey が無いシノニムなどの親推定に使う
      const childPreferredParent = new Map(); // child -> {parent, uncertain}
      entries.forEach((e) => {
        if (!e.normalizedChild) return;
        const list = childPreferredParent.get(e.normalizedChild) || [];
        list.push(e);
        childPreferredParent.set(e.normalizedChild, list);
      });
      const resolvePreferredParent = (child) => {
        const list = childPreferredParent.get(child) || [];
        if (list.length === 0) return { parent: null, uncertain: false };
        const scoreMap = new Map();
        list.forEach((e) => {
          const p = e.normalizedParent;
          if (!p) return;
          const weight = e.dbEntry ? 4 : (e.rank && e.rank !== 'synonym' ? 2 : 1);
          scoreMap.set(p, (scoreMap.get(p) || 0) + weight);
        });
        if (scoreMap.size === 0) return { parent: null, uncertain: false };
        let bestParent = null;
        let bestScore = -1;
        scoreMap.forEach((score, parent) => {
          if (score > bestScore) {
            bestParent = parent;
            bestScore = score;
          } else if (score === bestScore && bestParent !== parent) {
            bestParent = parent; // keep one, but mark uncertain
          }
        });
        const scores = [...scoreMap.values()];
        const topCount = scores.filter((s) => s === bestScore).length;
        return { parent: bestParent, uncertain: topCount > 1 };
      };

      const processedKeys = new Set();
      const deriveAcceptedNormalization = (gbifRecord) => {
        if (!gbifRecord) return { parent: null, child: null };
        const acceptedKey = gbifRecord.acceptedKey || gbifRecord.acceptedUsageKey;
        const fromMap = acceptedKey ? gbifKeyToNormalized.get(String(acceptedKey)) : null;
        let parentNorm = fromMap?.parent || null;
        let childNorm = fromMap?.child || null;

        const acceptedName = gbifRecord.acceptedCanonicalName || gbifRecord.acceptedScientificName || null;
        if (acceptedName) {
          const tokens = normalizeWhitespace(String(acceptedName)).split(/\s+/).filter(Boolean);
          const genusToken = tokens[0];
          if (!parentNorm && genusToken) {
            parentNorm = normalizeParentKey(genusToken, { fallbackToRoot: false }) || genusToken.toLowerCase();
          }
          const childForNorm = tokens.length > 1 ? tokens.slice(1).join(' ') : genusToken || '';
          if (!childNorm && childForNorm) {
            childNorm = normalizeNameForComparison(childForNorm, genusToken);
          }
        }

        return { parent: parentNorm, child: childNorm };
      };

      const processEntries = (filterFn) => {
        entries.forEach(({ key, entry, normalizedParent, normalizedChild, dbEntry, gbifGroup, primaryGbif, rank }) => {
          if (processedKeys.has(key)) return;
          if (!filterFn(rank)) return;

          const gbifUsageKey = primaryGbif ? String(primaryGbif.key) : null;

          // synonym・その他非標準ランクは acceptedKey/acceptedName から親子を再解決
          let effectiveNormalizedParent = normalizedParent;
          let effectiveNormalizedChild = normalizedChild;
          const lowerRank = (rank || '').toLowerCase();
          const shouldResolveByAccepted = lowerRank === 'synonym' || (!validRankSet.has(lowerRank) && lowerRank);
          let uncertainParent = false;
          if (shouldResolveByAccepted) {
            const { parent: accParent, child: accChild } = deriveAcceptedNormalization(primaryGbif);
            if (accParent) effectiveNormalizedParent = accParent;
            if (accChild) effectiveNormalizedChild = accChild;
            if (!effectiveNormalizedParent && primaryGbif?.acceptedKey) {
              const accNorm = gbifKeyToNormalized.get(String(primaryGbif.acceptedKey));
              if (accNorm?.parent) effectiveNormalizedParent = accNorm.parent;
              if (!effectiveNormalizedChild && accNorm?.child) effectiveNormalizedChild = accNorm.child;
            }
            if (!accParent) {
              const pref = resolvePreferredParent(effectiveNormalizedChild);
              if (pref.parent) {
                effectiveNormalizedParent = pref.parent;
                uncertainParent = pref.uncertain;
              }
            }
            // acceptedKey で解決できない場合でも、parentKey から属などを拾う
            if (!effectiveNormalizedParent && primaryGbif?.parentKey) {
              const parentInfo = gbifKeyToNormalized.get(String(primaryGbif.parentKey));
              if (parentInfo?.child) {
                effectiveNormalizedParent = parentInfo.child;
                uncertainParent = true;
              }
            }
            // 同一グループ内に acceptedKey のレコードが含まれていればそれを利用
            if (!effectiveNormalizedParent && primaryGbif?.acceptedKey) {
              const accFromGroup = gbifGroup.find(g => String(g.key) === String(primaryGbif.acceptedKey));
              if (accFromGroup?._normalized_parent) {
                effectiveNormalizedParent = accFromGroup._normalized_parent;
                uncertainParent = true;
              }
              if (accFromGroup?._normalized_child && !effectiveNormalizedChild) {
                effectiveNormalizedChild = accFromGroup._normalized_child;
              }
            }
          }
          if (!effectiveNormalizedParent && effectiveNormalizedChild) {
            const pref = resolvePreferredParent(effectiveNormalizedChild);
            if (pref.parent) {
              effectiveNormalizedParent = pref.parent;
              uncertainParent = pref.uncertain;
            }
          }

          // シノニムキー(syn-*)が混入している場合は親名判定用に除去する
          if (effectiveNormalizedParent && effectiveNormalizedParent.startsWith('syn-')) {
            effectiveNormalizedParent = effectiveNormalizedParent.slice(4);
          }

          let parentDbIdResolved = resolvedParentIdMap.get(effectiveNormalizedParent) || parent.id || parent.db_id || null;
          const parentDisplay = dbEntry?.parent_name
            || primaryGbif?.genus
            || primaryGbif?.parent
            || parent.name;
          const parentIdDisplay = dbEntry?.current_parent || primaryGbif?.parentKey || '';

          const candidate = {
            id: gbifUsageKey || (dbEntry ? dbEntry.id : key),
            type: 'HEADER',
            selection_id: gbifUsageKey || (dbEntry ? dbEntry.id : key),
            match_status: dbEntry ? (gbifGroup.length ? 'MERGED' : 'DB_ONLY') : 'NEW',
            rank: dbEntry?.current_rank || primaryGbif?.rank || '',
            primary_id: dbEntry ? dbEntry.id : (gbifUsageKey ? `(new)-${gbifUsageKey}` : `(new)-${key}`),
            db_record: dbEntry
              ? { ...dbEntry, parent_name: dbEntry.parent_name }
              : null,
            gbif_records: gbifGroup.map(g => ({ ...g, excluded: false })),
            gbif_usage_key: gbifUsageKey ? [gbifUsageKey] : null,
            _normalized_child: effectiveNormalizedChild,
            _normalized_parent: effectiveNormalizedParent,
            _resolved_parent_db_id: parentDbIdResolved,
            _uncertain_parent: uncertainParent,
          };

          candidates.push(candidate);
          if (dbEntry?.id) {
            resolvedParentIdMap.set(effectiveNormalizedChild, dbEntry.id);
          }
          processedKeys.add(key);
        });
      };

      // VALID階層を上から処理
      rankOrder.forEach(r => processEntries(rank => rank === r));
      // シノニムは最後に
      processEntries(rank => rank === 'synonym');
      // その他（未知ランク）は最後にまとめて
      processEntries(() => true);

      // 4) 同一キー（正規化親＋子）で最良の候補に集約する後処理
      const scoreStatus = (s) => (s === 'MERGED' ? 3 : s === 'DB_ONLY' ? 2 : 1);
      const canonicalMap = new Map(); // key -> {candidate, gbifExtras: []}

      candidates.forEach((c) => {
        const key = `${c._normalized_parent || ''}||${c._normalized_child || ''}`;
        if (!c._normalized_child || !c._normalized_parent) {
          // 正規化できていないものはそのままキー無しで扱う
          canonicalMap.set(Symbol(), { candidate: c, gbifExtras: [] });
          return;
        }
        const existing = canonicalMap.get(key);
        if (!existing) {
          canonicalMap.set(key, { candidate: c, gbifExtras: [] });
          return;
        }
        const current = existing.candidate;
        const currentScore = scoreStatus(current.match_status);
        const nextScore = scoreStatus(c.match_status);
        if (nextScore > currentScore) {
          existing.gbifExtras.push(...(current.gbif_records || []));
          existing.candidate = c;
        } else {
          existing.gbifExtras.push(...(c.gbif_records || []));
        }
      });

      const mergeByParentId = (list) => {
        const mergeMap = new Map();
        const withoutKey = [];

        list.forEach((c) => {
          const parentKey = (c._resolved_parent_db_id && c._normalized_child)
            ? `${c._resolved_parent_db_id}||${c._normalized_child}`
            : null;
          if (!parentKey) {
            withoutKey.push(c);
            return;
          }
          const existing = mergeMap.get(parentKey);
          if (!existing) {
            mergeMap.set(parentKey, { candidate: c, extras: [] });
            return;
          }
          const currentScore = scoreStatus(existing.candidate.match_status);
          const nextScore = scoreStatus(c.match_status);
          if (nextScore > currentScore) {
            existing.extras.push(...(existing.candidate.gbif_records || []));
            existing.candidate = c;
          } else {
            existing.extras.push(...(c.gbif_records || []));
          }
        });

        const merged = [];
        mergeMap.forEach(({ candidate, extras }) => {
          if (extras.length > 0) {
            const seen = new Set((candidate.gbif_records || []).map(g => String(g.key)));
            const mergedGbif = [...(candidate.gbif_records || [])];
            extras.forEach(g => {
              const k = String(g.key);
              if (seen.has(k)) return;
              seen.add(k);
              mergedGbif.push(g);
            });
            candidate.gbif_records = mergedGbif;
          }
          merged.push(candidate);
        });

        return [...merged, ...withoutKey];
      };

      const finalCandidates = [];
      canonicalMap.forEach(({ candidate, gbifExtras }) => {
        if (gbifExtras.length > 0) {
          const mergedGbif = [
            ...(candidate.gbif_records || []),
            ...gbifExtras,
          ];
          // dedupe by key
          const seen = new Set();
          candidate.gbif_records = mergedGbif.filter(g => {
            const k = String(g.key);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          });
        }
        finalCandidates.push(candidate);
      });

      const mergedFinalCandidates = mergeByParentId(finalCandidates);

      console.debug("Descendant candidates fetched:", mergedFinalCandidates);

      return mergedFinalCandidates;
    } catch (err) {
       console.error("Failed to get descendant candidates:", err);
       throw err; // Re-throw to be caught by parent
    }
  }, [fetchMode]);

  const fetchReviewCandidates = useCallback(async (parent) => {
    const parentDbId = parent?.db_id;
    if (!parentDbId) {
      setError("親のデータベースIDが見つかりません。");
      return;
    }

    setLoading(true);
    setError("");
    setReviewCandidates([]);
 
    try {
      const candidates = [];

      // Part 1: Fetch "self"
      if (fetchMode === 'self_only' || fetchMode === 'self_and_descendants') {
        const { data: parentDbRecord, error: dbErr } = await supabase
          .from('scientific_names')
          .select('id, name_spell_valid, gbif_id, gbif_usage_key, merged_gbif_ids, authors_for_search, authority_year, current_rank, current_parent, parent:current_parent(name_spell_valid)')
          .eq('id', parent.db_id)
          .single();
        if (dbErr) throw dbErr;

        const parentWithParentName = { ...parentDbRecord, parent_name: parentDbRecord.parent?.name_spell_valid };
        delete parentWithParentName.parent;

        const gbifForParent = await searchGbifByName(parentDbRecord.name_spell_valid);

        // GBIFの結果から最適なusage keyを見つける (Backboneを優先)
        const backboneRecord = gbifForParent.find(g => g.datasetKey === GBIF_BACKBONE_DATASET_KEY);
        const bestUsageKey = backboneRecord ? String(backboneRecord.key) : parentDbRecord.gbif_usage_key;

        candidates.push({
          id: `self-${parent.db_id}`,
          type: 'HEADER',
          selection_id: `self-${parent.db_id}`,
          match_status: 'MERGED',
          rank: parentDbRecord.current_rank,
          primary_id: parentDbRecord.id,
          db_record: parentWithParentName,
          gbif_records: gbifForParent.map(g => ({ ...g, excluded: false })),
          gbif_usage_key: bestUsageKey,
        });
      }

      // Part 2: Fetch "descendants"
      if (fetchMode === 'descendants_only' || fetchMode === 'self_and_descendants') {
        const descendantCandidates = await getDescendantCandidates(parent, fetchDepth);
        candidates.push(...descendantCandidates);
      }

      // Deduplicate candidates in case 'self' was also found as a 'descendant' (e.g. via synonyms)
      const finalCandidates = [];
      const seenIds = new Set();
      candidates.forEach(c => {
        if (!seenIds.has(c.id)) {
          finalCandidates.push(c);
          seenIds.add(c.id);
        }
      });

      setReviewCandidates(finalCandidates);

    } catch (err) {
       setError("レビュー候補の取得に失敗しました: " + err.message);
    } finally {
       setLoading(false);
    }
  }, [fetchMode, fetchDepth, searchGbifByName, getDescendantCandidates]);
  const getActiveGbifRecords = useCallback((row) => (row?.gbif_records || []).filter(g => !g.excluded), []);

  const pickBestAuthorshipYear = useCallback((records = []) => {
    let bestAuthorship = null;
    let bestYear = null;
    records.forEach((r) => {
      if (!bestAuthorship) {
        const a = getGbifAuthorship(r);
        if (a) bestAuthorship = a;
      }
      if (!bestYear) {
        const y = getGbifYear(r);
        if (y) bestYear = y;
      }
    });
    return { authorship: bestAuthorship, year: bestYear };
  }, []);

  const computeMatchStatus = useCallback((row) => {
    const activeGbif = getActiveGbifRecords(row);
    if (row.db_record) {
      return activeGbif.length ? 'MERGED' : 'DB_ONLY';
    }
    return activeGbif.length ? 'NEW' : 'NEW';
  }, [getActiveGbifRecords]);

  const handleRegisterWithConfirmation = () => {
    if (selectionModel.length === 0) return;
    setConfirmDialogContent({
        title: '登録の確認',
        description: `選択した ${selectionModel.length} 件を登録します。よろしいですか？`,
        onConfirm: () => {
            setConfirmDialogOpen(false);
            handleRegister();
        }
    });
    setConfirmDialogOpen(true);
  };

  const handleRegister = useCallback(async () => {
    if (selectionModel.length === 0) {
      setError("登録するレコードが選択されていません。");
      return;
    }
    setLoading(true);
    setError("");

    // This check is important because currentParent might not be set if the user navigates weirdly.
    // It should be set in Step 3 before getting to this point.
    if (!currentParent?.db_id) {
      setError("登録の起点となる親が設定されていません。Step 3からやり直してください。");
      setLoading(false);
      return;
    }

    const payload = selectionModel.map(selectedId => {
      const header = reviewCandidates.find(c => c.type === 'HEADER' && c.selection_id === selectedId);
      if (!header) return null;

      const activeGbif = getActiveGbifRecords(header);
      const candidateUsageKey = header.gbif_usage_key; // 候補行に保存されたUsageKeyを直接使用

      const sources_to_add = activeGbif.map(g => ({
          gbif_key: String(g.key),
          dataset_key: g.datasetKey,
          crawled_at: new Date().toISOString(),
      }));

      if (header.match_status === 'MERGED') {
        if (activeGbif.length === 0) return null;

        const dbRecord = header.db_record;
        const primaryGbifRecord = activeGbif.find(g => String(g.key) === candidateUsageKey) || activeGbif[0];
        const bestGbifMeta = pickBestAuthorshipYear(activeGbif);

        const payloadPart = {
          db_id_to_merge: header.primary_id,
          primary_gbif_id: String(primaryGbifRecord.key),
          gbif_ids_to_add: activeGbif.map(g => String(g.key)),
          gbif_usage_key: candidateUsageKey ? [candidateUsageKey] : null,
          sources_to_add: sources_to_add,
        };

        // DBに情報がなければGBIFから補完する
        if (!dbRecord?.authors_for_search && bestGbifMeta.authorship) {
          payloadPart.authorship = bestGbifMeta.authorship;
        }
        if (!dbRecord?.authority_year && bestGbifMeta.year) {
          payloadPart.year = bestGbifMeta.year;
        }
        return payloadPart;
      } else if (header.match_status === 'NEW') {
        if (activeGbif.length === 0) return null;
        // 候補行のUsageKeyに一致するGBIFレコードを優先、なければ最初のGBIFレコードを使用
        const gbifRecord = activeGbif.find(g => String(g.key) === candidateUsageKey) || activeGbif[0];
        const bestGbifMeta = pickBestAuthorshipYear(activeGbif);
        const name = normalizeNameForComparison(gbifRecord.canonicalName || gbifRecord.scientificName, currentParent?.name);
        const authorship = bestGbifMeta.authorship || getGbifAuthorship(gbifRecord);
        const year = bestGbifMeta.year || getGbifYear(gbifRecord);

        // 親のDB IDを解決する。優先順位は以下の通り：
        // 1. 現在のレビューリスト内で、この新規レコードの親となる候補がDBレコードを持つ場合、そのDB IDを使用。
        // 2. それ以外の場合、ヘッダーに解決済みの親DB IDがあればそれを使用（getDescendantCandidatesで解決済み）。
        // 3. 最終的に、インポート操作全体の現在の親のDB IDを使用。
        let parentDbId = header._resolved_parent_db_id || currentParent?.db_id || null;
        const nameOriginal = `${gbifRecord.canonicalName || gbifRecord.scientificName || name} (temp data)`;

        // 親を名寄せキーから逆引き（同一レビューリスト内で既にマージ/DB登録済みの親を優先）
        const parentKeyForRow = header._normalized_parent;
        if (parentKeyForRow) {
          const parentCandidate = reviewCandidates.find(c => 
            c.type === 'HEADER' && c._normalized_child === parentKeyForRow && c.db_record?.id);
          if (parentCandidate) {
            parentDbId = parentCandidate.db_record.id; // 上書き
          }
        }

        return {
          parent_db_id: parentDbId,
          name: name,
          name_original: nameOriginal,
          authorship: authorship,
          year: year,
          rank: gbifRecord.rank,
          primary_gbif_id: String(gbifRecord.key),
          gbif_ids_to_add: activeGbif.map(g => String(g.key)),
          gbif_usage_key: candidateUsageKey ? [candidateUsageKey] : null,
          sources_to_add: sources_to_add,
        };
      }
      return null;
    }).filter(Boolean);

    // --- Pre-flight check for duplicate GBIF IDs ---
    const recordsToCheck = payload.map(p => {
        const header = reviewCandidates.find(c => c.selection_id === (p.db_id_to_merge || p.primary_gbif_id));
        return {
            db_id: p.db_id_to_merge || null,
            gbif_id: p.primary_gbif_id,
            name: p.name || header?.db_record?.name_spell_valid || 'Unknown',
        };
    }).filter(p => p.gbif_id);

    if (recordsToCheck.length > 0) {
        const allGbifIds = [...new Set(recordsToCheck.map(r => r.gbif_id))];
        const { data: existingNames, error: checkError } = await supabase
            .from('scientific_names')
            .select('id, gbif_id, name_spell_valid')
            .in('gbif_id', allGbifIds);

        if (checkError) {
            setError(`Failed to pre-check for duplicate GBIF IDs: ${checkError.message}`);
            setLoading(false);
            return;
        }

        if (existingNames && existingNames.length > 0) {
            const conflicts = [];
            for (const existing of existingNames) {
                const conflictSources = recordsToCheck.filter(p => String(p.gbif_id) === String(existing.gbif_id));
                for (const source of conflictSources) {
                    if (String(source.db_id) !== String(existing.id)) {
                        conflicts.push(`GBIF ID ${existing.gbif_id} is already used by "${existing.name_spell_valid}" (DB ID: ${existing.id}). Cannot assign it to "${source.name}".`);
                    }
                }
            }
            
            if (conflicts.length > 0) {
                setError(`Cannot proceed due to GBIF ID conflicts. Please resolve them before registering.\n\n${[...new Set(conflicts)].join('\n')}`);
                setLoading(false);
                return;
            }
        }
    }
    // --- End of pre-flight check ---

    if (payload.length === 0) {
      setError("登録対象のデータがありません。");
      setLoading(false);
      return;
    }

    try {
      const { data, error: err } = await supabase.rpc('register_approved_taxa', {
        p_taxa_to_register: payload
      });
      if (err) throw err;

      setRegistrationResult(data);
      setActiveStep(4);
      setSelectionModel([]);
    } catch (err) {
      setError("登録に失敗しました: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectionModel, reviewCandidates, currentParent, getActiveGbifRecords]);

  const handleDescend = (row) => {
    // Descend should only be possible for MERGED or DB_ONLY groups
    const status = computeMatchStatus(row);
    if (row.type !== 'HEADER' || !row.primary_id || status === 'NEW') {
        showStatus({ severity: 'warning', title: '未登録', description: 'この学名はまだデータベースに登録されていません。登録後に下の階層へ進めます。' });
        return;
    }
    const activeGbif = getActiveGbifRecords(row);
    if (activeGbif.length === 0) {
      showStatus({ severity: 'warning', title: 'GBIFキーなし', description: '下位を取得するためのGBIFキーがありません。' });
      return;
    }
    const gbifKeysSet = new Set(activeGbif.map((g) => String(g.key)));
    (row.db_record?.merged_gbif_ids || []).forEach((k) => k && gbifKeysSet.add(String(k)));
    const gbifKeys = Array.from(gbifKeysSet);
    if (gbifKeys.length === 0) {
      showStatus({ severity: 'warning', title: 'GBIFキーなし', description: '下位を取得するためのGBIFキーがありません。' });
      return;
    }
    const displayName = row.db_record?.name || getGbifDisplayName(activeGbif[0] || {}) || row.primary_id;
    const newNode = { db_id: row.primary_id, name: displayName, gbif_key: gbifKeys, gbif_usage_key: row.gbif_usage_key?.length > 0 ? row.gbif_usage_key : null };

    if (history.length === 0 || history[history.length - 1].db_id !== newNode.db_id) {
      setHistory(prev => [...prev, newNode]);
    }
    setCurrentParent(newNode);
    setSelectionModel([]);
    fetchReviewCandidates(newNode);
  };

  const handleBreadcrumbClick = (node, index) => {
    setHistory(prev => prev.slice(0, index + 1));
    setCurrentParent(node);
    setSelectionModel([]);
    fetchReviewCandidates(node);
  };

  const handleOpenLinkDialog = (candidate) => {
    // candidate is a HEADER row
    setCandidateToLink(candidate); 
    setLinkDialogOpen(true);
  };

  const handleCloseLinkDialog = useCallback(() => {
    setLinkDialogOpen(false);
    setCandidateToLink(null);
    setLinkDialogSearchQuery("");
    setLinkDialogSearchResults([]);
    setLinkDialogSearchLoading(false);
  }, []);

  const handleLinkCandidate = useCallback(async (selectedDbId) => {
    if (!candidateToLink || !selectedDbId) {
      handleCloseLinkDialog();
      return;
    }

    try {
      const gbifKeysToAdd = (candidateToLink.gbif_records || []).map(g => String(g.key));
      if (gbifKeysToAdd.length === 0) {
        throw new Error("リンクするGBIFキーが見つかりません。");
      }

      const { error } = await supabase.rpc('add_gbif_keys_to_name', {
        p_name_id: selectedDbId,
        p_gbif_keys: gbifKeysToAdd,
        p_usage_key: candidateToLink.gbif_usage_key ? [candidateToLink.gbif_usage_key] : null,
      });

      if (error) {
        throw new Error(`リンク処理に失敗しました: ${error.message}`);
      }

      // The simplest and most robust way to reflect the change is to refetch the data.
      showStatus({ severity: 'info', title: 'リンク成功', description: '手動リンクが設定されました。データを再読み込みしています。' });
      fetchReviewCandidates(currentParent);
      handleCloseLinkDialog();
    } catch (err) {
      showStatus({ severity: 'error', title: 'リンク失敗', description: '学名情報の取得に失敗しました: ' + err.message });
    }
  }, [candidateToLink, showStatus, handleCloseLinkDialog, fetchReviewCandidates, currentParent]);

  const buildDisplayLines = useCallback((row) => {
    const activeGbif = getActiveGbifRecords(row);
    const merged = (() => {
      const primaryGbif = activeGbif[0] || null;
      const bestGbifMeta = pickBestAuthorshipYear(activeGbif);
      const db = row?.db_record;
      const parentNameForNormalization = db?.parent_name || row?.parent_name || currentParent?.name || '';
      const normalizedGbifName = !db && primaryGbif
        ? normalizeNameForComparison(getGbifDisplayName(primaryGbif), parentNameForNormalization)
        : null;

      // 親IDを解決するロジックを登録時と表示時で共通化する
      // 1. まず、getDescendantCandidatesで解決されたIDをデフォルトとして使用
      let resolvedParentId = row?._resolved_parent_db_id;
      // 2. 次に、現在のレビューリスト全体を検索し、親の正規化名に一致するDBレコードがあれば、そのIDで上書きする
      //    これにより、ユーザーがレビュー画面で手動でマージした結果が即座に子の親IDに反映される
      const parentKeyForRow = row?._normalized_parent;
      if (parentKeyForRow) {
          const parentCandidate = reviewCandidates.find(c =>
            c.type === 'HEADER' && c._normalized_child === parentKeyForRow && c.db_record?.id);
          if (parentCandidate) {
            resolvedParentId = parentCandidate.db_record.id;
          }
      }

      return {
        status: computeMatchStatus(row),
        rank: db?.current_rank || primaryGbif?.rank || row.rank || '',
        primary_id: db?.id || (primaryGbif ? String(primaryGbif.key) : row?.primary_id),
        original_id: db?.id || (primaryGbif ? String(primaryGbif.key) : ''),
        gbif_status: primaryGbif?.taxonomicStatus || (db ? 'N/A' : ''),
        name: db?.name_spell_valid || normalizedGbifName || (primaryGbif ? getGbifDisplayName(primaryGbif) : ''),
        parent_name: db?.parent_name || primaryGbif?.parent || '',
        parent_id: db?.current_parent || resolvedParentId || primaryGbif?.parentKey || '',
        author: db?.authors_for_search || bestGbifMeta.authorship || getGbifAuthorship(primaryGbif || {}) || '',
        year: db?.authority_year || bestGbifMeta.year || getGbifYear(primaryGbif || {}) || '',
        source: '登録用',
      };
    })();

    const lines = [{ key: 'merged', label: '登録用', type: 'MERGED', data: merged }];
    if (row?.db_record) {
      lines.push({ key: 'db', label: 'Database', type: 'DB', data: row.db_record });
    }
    activeGbif.forEach((g, idx) => {
      lines.push({ key: `gbif-${g.key || idx}`, label: getDatasetName(g) || 'GBIF', type: 'GBIF', data: g });
    });
    return { lines, merged };
  }, [computeMatchStatus, getActiveGbifRecords, getDatasetName, normalizeNameForComparison, currentParent, reviewCandidates, pickBestAuthorshipYear]);

  const getFieldValue = (field, line, merged, row) => {
    if (field === 'source') return line.label; // buildDisplayLinesで設定されたラベルを使用
    if (field === 'status') return line.type === 'MERGED' ? merged.status : '';
    if (field === 'primary_id') return line.type === 'MERGED' ? merged.primary_id : '';

    if (line.type === 'MERGED') {
      if (field === 'rank') return merged.rank;
      return merged[field] ?? '';
    }

    if (line.type === 'DB') {
      const db = line.data || {};
      switch (field) {
        case 'original_id': return db.id || '';
        case 'gbif_status': return 'N/A';
        case 'name': return db.name_spell_valid || '';
        case 'parent_name': return db.parent_name ?? row?.db_record?.parent_name ?? '';
        case 'parent_id': return db.current_parent ?? row?.db_record?.current_parent ?? '';
        case 'author': return db.author || db.authors_for_search || '';
        case 'year': return db.year || db.authority_year || '';
        case 'rank': return db.current_rank || '';
        default: return '';
      }
    }

    // GBIF line
    const g = line.data || {};
    switch (field) {
      case 'original_id': return g.key ? String(g.key) : '';
      case 'gbif_status': return g.taxonomicStatus || '';
      case 'name': return getGbifDisplayName(g);
      case 'parent_name': return g.parent || '';
      case 'parent_id': return g.parentKey || '';
      case 'author': return getGbifAuthorship(g);
      case 'year': return getGbifYear(g) || '';
      case 'rank': return g.rank || '';
      default: return '';
    }
  };

  const renderFieldLines = (row, field, { chipForMerged = false, showSources = true } = {}) => {
    const { lines, merged } = buildDisplayLines(row);
    const filteredLines = showSources ? lines : lines.filter((l) => l.type === 'MERGED');
    return (
      <Stack spacing={0.2} sx={{ py: 0.3 }}>
        {filteredLines.map((line, idx) => {
          const value = getFieldValue(field, line, merged, row);
          const isBackbone = line.type === 'GBIF' && line.data?.datasetKey === GBIF_BACKBONE_DATASET_KEY;

          let content;
          if (chipForMerged && line.type === 'MERGED' && value) {
            content = <Chip label={value} color={value === 'NEW' ? 'success' : value === 'MERGED' ? 'primary' : 'default'} size="small" />;
          } else {
            content = <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre',
                  overflowWrap: 'normal',
                  wordBreak: 'keep-all',
                  lineHeight: 1.3,
                  fontWeight: line.type === 'MERGED' ? 700 : 400,
                }}
              >
                {value || '\u00a0'}
              </Typography>;
          }

          return (
            <Box key={line.key || idx} sx={{ px: 0.4, borderRadius: 0.5, backgroundColor: lineBg(line.type) }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {field === 'name' && isBackbone && (
                  <Tooltip title="GBIF Backbone">
                    <VerifiedUserIcon fontSize="inherit" sx={{ color: 'success.light', verticalAlign: 'middle' }} />
                  </Tooltip>
                )}
                {field === 'parent_name' && row?._uncertain_parent && line.type === 'MERGED' && (
                  <Tooltip title="親を自動推定しています。確認してください。">
                    <Chip label="?" size="small" color="warning" variant="outlined" />
                  </Tooltip>
                )}
                {content}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };

  // Keep view dialog in sync with latest row state
  useEffect(() => {
    if (!viewTargetRow) return;
    const latest = reviewCandidates.find((r) => r.id === viewTargetRow.id);
    if (latest) {
      setViewTargetRow(latest);
    }
  }, [reviewCandidates, viewTargetRow]);

  const handleViewSelectionToggle = (key) => {
    setViewSelection((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const updateRowGbifRecords = (rowId, updater) => {
    setReviewCandidates((prev) =>
      prev.map((row) => (row.id === rowId ? updater(row) : row))
    );
  };

  const headerRows = useMemo(() => reviewCandidates.filter((r) => r.type === 'HEADER'), [reviewCandidates]);
  const rankStats = useMemo(() => {
    const stats = {};
    headerRows.forEach((row) => {
      const { merged } = buildDisplayLines(row);
      const rank = String(merged.rank || 'N/A').toUpperCase();
      if (rank === 'UNRANKED') return;
      stats[rank] = (stats[rank] || 0) + 1;
    });
    return stats;
  }, [headerRows, buildDisplayLines]);

  const handleExcludeSelected = () => {
    if (!viewTargetRow || viewSelection.length === 0) return;
    const selectionSet = new Set(viewSelection.map(String));
    updateRowGbifRecords(viewTargetRow.id, (row) => {
      const nextGbif = (row.gbif_records || []).map((g) =>
        selectionSet.has(String(g.key)) ? { ...g, excluded: true } : g
      );
      const nextRow = { ...row, gbif_records: nextGbif };
      return { ...nextRow, match_status: computeMatchStatus(nextRow) };
    });
    setViewSelection([]);
  };

  const handleIncludeSelected = () => {
    if (!viewTargetRow || viewSelection.length === 0) return;
    const selectionSet = new Set(viewSelection.map(String));
    updateRowGbifRecords(viewTargetRow.id, (row) => {
      const nextGbif = (row.gbif_records || []).map((g) =>
        selectionSet.has(String(g.key)) ? { ...g, excluded: false } : g
      );
      const nextRow = { ...row, gbif_records: nextGbif };
      return { ...nextRow, match_status: computeMatchStatus(nextRow) };
    });
    setViewSelection([]);
  };

  const handleOpenViewDialog = (row) => {
    setViewTargetRow(row);
    setViewDialogOpen(true);
    setViewSelection([]);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewTargetRow(null);
    setViewSelection([]);
  };

  const handleSelectAllHeaders = useCallback(() => {
    if (headerRows.length === 0) return;
    setSelectionModel(headerRows.map((row) => row.selection_id));
  }, [headerRows]);

  const handleClearSelection = useCallback(() => {
    setSelectionModel([]);
  }, []);

  const handleMergeSelection = () => {
    if (selectionModel.length === 0) {
      setError("マージ対象のレコードが選択されていません。");
      return;
    }

    const selectedSet = new Set(selectionModel);
    const dedupeGbifRecords = (records = []) => {
      const seen = new Set();
      const mergedList = [];
      records.forEach((g) => {
        const key = String(g.key);
        if (seen.has(key)) return;
        seen.add(key);
        mergedList.push({ ...g, excluded: false });
      });
      return mergedList;
    };
    const buildRowMergeKey = (row) => {
      const { merged } = buildDisplayLines(row);
      const rank = (merged.rank || '').toLowerCase();
      const nameKey = normalizeNameForComparison(merged.name || '', merged.parent_name || '');
      const parentNameKey = (merged.parent_name || '').toLowerCase();
      const dbId = row.db_record?.id || '';
      return `${rank}||${nameKey}||${parentNameKey}||${dbId}`;
    };

    const mergeGroups = new Map();
    reviewCandidates.forEach((row, index) => {
      if (row.type !== 'HEADER' || !selectedSet.has(row.selection_id)) return;
      const mergeKey = buildRowMergeKey(row);
      const gbifRecords = dedupeGbifRecords(row.gbif_records || []);

      if (!mergeGroups.has(mergeKey)) {
        mergeGroups.set(mergeKey, { row: { ...row, gbif_records: gbifRecords }, firstIndex: index });
      } else {
        const target = mergeGroups.get(mergeKey);
        const combined = dedupeGbifRecords([...(target.row.gbif_records || []), ...gbifRecords]);
        target.row = { ...target.row, gbif_records: combined };
      }
    });

    const pushedKeys = new Set();
    const nextRows = [];
    reviewCandidates.forEach((row) => {
      if (row.type !== 'HEADER' || !selectedSet.has(row.selection_id)) {
        nextRows.push(row);
        return;
      }
      const mergeKey = buildRowMergeKey(row);
      if (pushedKeys.has(mergeKey)) return;
      const group = mergeGroups.get(mergeKey);
      if (!group) return;
      const mergedRow = { ...group.row };
      mergedRow.match_status = computeMatchStatus(mergedRow);
      nextRows.push(mergedRow);
      pushedKeys.add(mergeKey);
    });

    const nextSelection = Array.from(pushedKeys).map((key) => mergeGroups.get(key)?.row.selection_id).filter(Boolean);
    setReviewCandidates(nextRows);
    setSelectionModel(nextSelection);
    showStatus?.({ severity: 'success', title: 'マージ完了', description: '選択したレコードをマージしました。' });
  };

  // --- Step 1: Handler for DB Parent Selection ---
  const handleDbParentSelect = useCallback(async (db_id) => {
    if (!db_id) {
      setDbParent(null);
      return;
    }
    setInitialParentSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from("scientific_names")
        .select("id, name_spell_valid, gbif_id, merged_gbif_ids, gbif_usage_key")
        .eq("id", db_id)
        .single();
      if (error) throw error;
      setDbParent({
        ...data,
        gbif_usage_key: Array.isArray(data.gbif_usage_key) ? data.gbif_usage_key : (data.gbif_usage_key ? [data.gbif_usage_key] : []),
      });
      setInitialParentSearchQuery(""); // Clear search
      setInitialParentSearchResults([]); // Clear results
    } catch (err) {
      setError("親の情報の取得に失敗しました: " + err.message);
    } finally {
      setInitialParentSearchLoading(false);
    }
  }, []);

  // --- Step 2: Effect to trigger GBIF search ---
  useEffect(() => {
    if (activeStep === 1 && dbParent?.name_spell_valid && gbifSearchResults.length === 0) {
      searchGbif(dbParent.name_spell_valid);
    }
  }, [activeStep, dbParent, searchGbif, gbifSearchResults.length]);

  const handleToggleGbifParentSelection = useCallback((key) => {
    if (!key) return;
    setGbifParentSelection((prev) => {
      const newIds = new Set(prev.ids);
      if (newIds.has(key)) newIds.delete(key);
      else newIds.add(key);
      return createSelectionModel(Array.from(newIds));
    });
  }, []);

  // --- Step 3: Handler to execute fetch ---
  const handleExecuteFetch = useCallback(async () => {
    if (!dbParent || gbifParentSelection.ids.size === 0) {
      setError("親のDB情報またはGBIF情報が不足しています。");
      return;
    }
    
    const selectedRecords = gbifSearchResults.filter(r => gbifParentSelection.ids.has(String(r.key)));
    const allKeys = selectedRecords.map(r => String(r.key));
    const backboneRecord = selectedRecords.find(r => r.datasetKey === GBIF_BACKBONE_DATASET_KEY);

    const usageKeysSet = new Set();
    if (backboneRecord) usageKeysSet.add(String(backboneRecord.key));
    if (Array.isArray(dbParent.gbif_usage_key)) dbParent.gbif_usage_key.forEach(key => usageKeysSet.add(String(key)));
    const finalGbifUsageKeys = Array.from(usageKeysSet);

    const parentForFetch = {
      db_id: dbParent.id,
      name: dbParent.name_spell_valid,
      gbif_key: allKeys,
      merged_gbif_ids: dbParent.merged_gbif_ids || [],
      gbif_usage_key: finalGbifUsageKeys.length > 0 ? finalGbifUsageKeys : null,
    };

    setCurrentParent(parentForFetch);
    setHistory([parentForFetch]);
    setFetchRequested(true);
    fetchReviewCandidates(parentForFetch);
  }, [dbParent, gbifParentSelection, gbifSearchResults, fetchReviewCandidates]);

  useEffect(() => {
    if (fetchRequested && !loading) {
      setFetchRequested(false);
      if (!error) setActiveStep(3);
    }
  }, [fetchRequested, loading, error]);

  const REVIEW_COLUMNS = [
     {
       field: 'selection', headerName: '', width: 50, sortable: false,
       renderCell: (params) => {
         if (params.row?.type !== 'HEADER') return null;
         const isSelected = selectionModel.includes(params.row.selection_id);
         return (
           <Checkbox
             checked={isSelected}
             onChange={(event) => {
               const newSelection = event.target.checked
                 ? [...selectionModel, params.row.selection_id]
                 : selectionModel.filter(id => id !== params.row.selection_id);
               setSelectionModel(newSelection);
             }}
           />
        );
      }
    },
     {
      field: "match_status", headerName: "Status", width: 100,
       renderCell: ({ row }) => renderFieldLines(row, 'status', { chipForMerged: true })
     },
     { field: "rank", headerName: "Rank", width: 100, renderCell: ({ row }) => renderFieldLines(row, 'rank') },
     { field: "primary_id", headerName: "Primary ID", width: 120, renderCell: ({ row }) => renderFieldLines(row, 'primary_id') },
     { field: "name", headerName: "Name", width: 150, renderCell: ({ row }) => renderFieldLines(row, 'name', { showSources: false }) },
     { field: "parent_name", headerName: "Parent Name", width: 150, renderCell: ({ row }) => renderFieldLines(row, 'parent_name', { showSources: false }) },
     { field: "source", headerName: "Source", width: 90, renderCell: ({ row }) => renderFieldLines(row, 'source') },
     { field: "original_id", headerName: "Original ID", width: 120, renderCell: ({ row }) => renderFieldLines(row, 'original_id') },
     { field: "gbif_status", headerName: "GBIF Status", width: 120, renderCell: ({ row }) => renderFieldLines(row, 'gbif_status') },
     { field: "parent_id", headerName: "Parent ID", width: 120, renderCell: ({ row }) => renderFieldLines(row, 'parent_id') },
     { field: "author", headerName: "Author", width: 150, renderCell: ({ row }) => renderFieldLines(row, 'author') },
     { field: "year", headerName: "Year", width: 80, renderCell: ({ row }) => renderFieldLines(row, 'year') },
     {
        field: 'view', headerName: 'View', width: 80, sortable: false,
        renderCell: ({ row }) => (
          <IconButton size="small" onClick={() => handleOpenViewDialog(row)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        )
     },
    {
       field: 'actions', headerName: 'Actions', width: 120, sortable: false,
        renderCell: (params) => (
            params.row?.type === 'HEADER' && (
            <Stack direction="row" spacing={0.5}>
                <Tooltip title="この階層へ移動">
                    <span>
                        <IconButton size="small" onClick={() => handleDescend(params.row)} disabled={params.row.match_status === 'NEW'}>
                            <ArrowDownwardIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                {params.row.match_status === 'NEW' && (
                     <Tooltip title="DB内の既存の学名にリンク">
                         <IconButton size="small" onClick={() => handleOpenLinkDialog(params.row)}>
                             <LinkIcon />
                         </IconButton>
                     </Tooltip>
                 )}
            </Stack>
            )
        ),
    },
  ];

  return (
    <Dialog open={open} onClose={() => onClose?.(false)} maxWidth="xl" fullWidth>
      <DialogTitle>GBIF 階層的インポート</DialogTitle>
      <DialogContent sx={{ position: 'relative', height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }}>
        {(loading || datasetMetaPending > 0) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 20,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeStep} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ px: 3 }}>
            <Tab label="Step 1: 親の選択" value={0} />
            <Tab label="Step 2: GBIF ID選択" value={1} disabled={activeStep < 1} />
            <Tab label="Step 3: 設定" value={2} disabled={activeStep < 2} />
            <Tab label="Step 4: レビュー" value={3} disabled={activeStep < 3} />
            <Tab label="Step 5: 結果" value={4} disabled={activeStep < 4} />
          </Tabs>
        </Box>

        <Box sx={{ p: 3, display: activeStep === 0 ? 'block' : 'none', overflowY: 'auto' }}>
          <Step1SelectDbParent
            dbParent={dbParent}
            initialParentSearchQuery={initialParentSearchQuery}
            initialParentSearchResults={initialParentSearchResults}
            initialParentSearchLoading={initialParentSearchLoading}
            onQueryChange={setInitialParentSearchQuery}
            onSelectDbParent={handleDbParentSelect}
            onClearDbParent={() => setDbParent(null)}
          />
        </Box>

        <Box sx={{ p: 3, display: activeStep === 1 ? 'block' : 'none', overflowY: 'auto' }}>
          <Step2SelectGbif
            dbParent={dbParent}
            gbifSearchError={gbifSearchError}
            gbifSearchLoading={gbifSearchLoading}
            displayedGbifResults={displayedGbifResults}
            gbifParentSelection={gbifParentSelection}
            onToggleGbifParent={handleToggleGbifParentSelection}
            datasetMetaCache={datasetMetaCache}
            gbifSortOrder={gbifSortOrder}
            onChangeSortOrder={setGbifSortOrder}
            gbifFreshnessFilter={gbifFreshnessFilter}
            onChangeFreshnessFilter={setGbifFreshnessFilter}
            getDatasetName={getDatasetName}
            getFreshnessCategory={getFreshnessCategory}
            GBIF_BACKBONE_DATASET_KEY={GBIF_BACKBONE_DATASET_KEY}
          />
        </Box>

        <Box sx={{ p: 3, display: activeStep === 2 ? 'block' : 'none', overflowY: 'auto' }}>
          <Step3FetchConfig
            dbParent={dbParent}
            selectedGbifCount={gbifParentSelection.ids.size}
            fetchMode={fetchMode}
            fetchDepth={fetchDepth}
            onChangeFetchMode={setFetchMode}
            onChangeFetchDepth={setFetchDepth}
          />
        </Box>

        <Box sx={{ p: 3, display: activeStep === 3 ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Step4Review
            currentParent={currentParent}
            history={history}
            onBreadcrumbClick={handleBreadcrumbClick}
            error={error}
            headerRows={headerRows}
            rankStats={rankStats}
            loading={loading}
            reviewCandidates={reviewCandidates}
            dataGridColumns={REVIEW_COLUMNS}
            dataGridStyles={dataGridStyles}
            onSelectAllHeaders={handleSelectAllHeaders}
            onClearSelection={handleClearSelection}
            selectionModel={selectionModel}
          />
        </Box>

        <Box sx={{ p: 3, display: activeStep === 4 ? 'block' : 'none', overflowY: 'auto' }}>
          <Step5Result
            registrationResult={registrationResult}
            onReset={resetState}
            onClose={() => onClose?.(false)}
          />
        </Box>

      </DialogContent>
      <DialogActions>
        {activeStep > 0 && activeStep < 4 && <Button onClick={() => setActiveStep(p => p - 1)} disabled={loading}>戻る</Button>}
        <Box sx={{ flex: '1 1 auto' }} />
        <Button onClick={() => onClose?.(false)}>閉じる</Button>
        {activeStep === 0 && <Button variant="contained" onClick={() => setActiveStep(1)} disabled={!dbParent}>次へ</Button>}
        {activeStep === 1 && <Button variant="contained" onClick={() => setActiveStep(2)} disabled={gbifParentSelection.ids.size === 0}>次へ</Button>}
        {activeStep === 2 && <Button variant="contained" onClick={handleExecuteFetch} disabled={loading}>{loading ? <CircularProgress size={24} /> : '実行'}</Button>}
        {activeStep === 3 && (
          <Button variant="outlined" onClick={handleMergeSelection} disabled={loading || selectionModel.length === 0}>選択をマージ</Button>
        )}
        {activeStep === 3 && (
          <Button variant="contained" onClick={handleRegisterWithConfirmation} disabled={loading || selectionModel.length === 0}>
            選択した {selectionModel.length} 件を登録
          </Button>
        )}
      </DialogActions>

      <LinkDialog
        open={linkDialogOpen}
        candidateToLink={candidateToLink}
        linkDialogSearchQuery={linkDialogSearchQuery}
        linkDialogSearchResults={linkDialogSearchResults}
        linkDialogSearchLoading={linkDialogSearchLoading}
        onClose={handleCloseLinkDialog}
        onSearchQueryChange={setLinkDialogSearchQuery}
        onLinkCandidate={handleLinkCandidate}
      />

      <ViewDialog
        open={viewDialogOpen}
        viewTargetRow={viewTargetRow}
        viewSelection={viewSelection}
        onToggleSelection={handleViewSelectionToggle}
        onClose={handleCloseViewDialog}
        onExcludeSelected={handleExcludeSelected}
        onIncludeSelected={handleIncludeSelected}
        getActiveGbifRecords={getActiveGbifRecords}
        getGbifDisplayName={getGbifDisplayName}
        GBIF_BACKBONE_DATASET_KEY={GBIF_BACKBONE_DATASET_KEY}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmDialogContent.title}
        description={confirmDialogContent.description}
        onCancel={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDialogContent.onConfirm}
      />

    </Dialog>
  );
}
